// src/pages/ForgotPassword.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Alert from '../components/common/Alert';
import PasswordField from '../components/forms/PasswordField';
import PhraseInputs from '../components/forms/PhraseInputs';
import { isValidPassword } from '../utils/validators';
import { AUTH_ENDPOINTS } from '../utils/constants';

import '../styles/global.css';
import '../styles/components/forgotPassword.css';

const ForgotPassword = () => {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('email-tab');

  // Email recovery state
  const [emailRecovery, setEmailRecovery] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: '',
    showOtp: false,
    showPasswordFields: false
  });

  // Phrase recovery state
  const [phraseRecovery, setPhraseRecovery] = useState({
    phrases: Array(12).fill(''),
    newPassword: '',
    confirmPassword: '',
    showOtp: false,
    otp: ''
  });

  // Button state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailBtnText, setEmailBtnText] = useState('Send Recovery OTP');
  const [phraseBtnText, setPhraseBtnText] = useState('Verify & Change Password');

  // Alert state
  const [alert, setAlert] = useState({
    isOpen: false,
    title: '',
    message: '',
    footer: '',
    type: 'success'
  });

  // Reset forms when switching tabs
  useEffect(() => {
    if (activeTab === 'email-tab') {
      resetEmailForm();
    } else {
      resetPhraseForm();
    }
  }, [activeTab]);

  const resetEmailForm = () => {
    setEmailRecovery({
      email: '',
      otp: '',
      newPassword: '',
      confirmPassword: '',
      showOtp: false,
      showPasswordFields: false
    });
    setEmailBtnText('Send Recovery OTP');
  };

  const resetPhraseForm = () => {
    setPhraseRecovery({
      phrases: Array(12).fill(''),
      newPassword: '',
      confirmPassword: '',
      showOtp: false,
      otp: ''
    });
    setPhraseBtnText('Verify & Change Password');
  };

  const showAlert = (title, message, type = 'success', footer = '') => {
    setAlert({
      isOpen: true,
      title,
      message,
      footer,
      type
    });

    if (type !== 'success') {
      setTimeout(() => {
        closeAlert();
      }, 5000);
    }
  };

  const closeAlert = () => {
    setAlert({ ...alert, isOpen: false });
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleEmailInputChange = (e) => {
    const { id, value } = e.target;
    setEmailRecovery(prev => ({ ...prev, [id]: value }));
  };

  const handlePhraseInputChange = (index, value) => {
    const newPhrases = [...phraseRecovery.phrases];
    newPhrases[index] = value;
    setPhraseRecovery(prev => ({ ...prev, phrases: newPhrases }));
  };

  const handlePhrasePasswordChange = (e) => {
    const { id, value } = e.target;
    setPhraseRecovery(prev => ({ ...prev, [id]: value }));
  };

  const handleEmailFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!emailRecovery.showOtp) {
        // Step 1: Request OTP
        await initiateEmailRecovery(emailRecovery.email);
      } else if (!emailRecovery.showPasswordFields) {
        // Step 2: Verify OTP
        await verifyEmailOTP(emailRecovery.email, emailRecovery.otp);
      } else {
        // Step 3: Reset Password
        await setNewPasswordWithEmail(
          emailRecovery.email,
          emailRecovery.otp,
          emailRecovery.newPassword,
          emailRecovery.confirmPassword
        );
      }
    } catch (error) {
      console.error('Email recovery error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhraseFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate inputs
      if (phraseRecovery.phrases.some(phrase => !phrase.trim())) {
        showAlert(
          'Input Error',
          'Please enter all 12 recovery phrases.',
          'fail',
          'All recovery phrases are required.'
        );
        return;
      }

      if (!phraseRecovery.newPassword || !phraseRecovery.confirmPassword) {
        showAlert(
          'Input Error',
          'Please enter and confirm your new password.',
          'fail',
          'Both password fields are required.'
        );
        return;
      }

      if (phraseRecovery.newPassword !== phraseRecovery.confirmPassword) {
        showAlert(
          'Password Mismatch',
          'New password and confirm password do not match.',
          'fail',
          'Please ensure both password fields are identical.'
        );
        return;
      }

      if (!isValidPassword(phraseRecovery.newPassword)) {
        showAlert(
          'Invalid Password',
          'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.',
          'fail',
          'Please choose a stronger password.'
        );
        return;
      }

      // Change password with mnemonic phrases
      await changePasswordWithMnemonics(phraseRecovery.phrases, phraseRecovery.newPassword);

    } catch (error) {
      console.error('Phrase recovery error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // API Functions
  const initiateEmailRecovery = async (email) => {
    try {
      setEmailBtnText('Sending...');

      // API call for initiating recovery
      const response = await fetch(AUTH_ENDPOINTS.INITIATE_RECOVERY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Server returned ${response.status}: ${response.statusText}`);
        } else {
          throw new Error(`Server returned non-JSON response (${response.status})`);
        }
      }

      const data = await response.json();

      if (data.success) {
        showAlert(
          'OTP Sent!',
          'A one-time password has been sent to your email.',
          'success',
          'Please enter it to continue the recovery process.'
        );
        setEmailRecovery(prev => ({ ...prev, showOtp: true }));
        setEmailBtnText('Verify OTP');
      } else {
        throw new Error(data.message || 'Failed to send recovery email');
      }
    } catch (error) {
      showAlert(
        'Email Recovery Failed',
        error.message || 'Failed to initiate password recovery.',
        'fail',
        'Please check your email address and try again.'
      );
      setEmailBtnText('Send Recovery OTP');
    }
  };

  const verifyEmailOTP = async (email, otp) => {
    try {
      if (!otp) {
        throw new Error('Please enter the OTP sent to your email');
      }

      setEmailBtnText('Verifying...');

      // Call the OTP verification endpoint
      const response = await fetch(AUTH_ENDPOINTS.VERIFY_RECOVERY_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, otp })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Server returned ${response.status}: ${response.statusText}`);
        } else {
          throw new Error(`Server returned non-JSON response (${response.status})`);
        }
      }

      const data = await response.json();

      if (data.success) {
        showAlert(
          'OTP Verified!',
          'Your identity has been verified.',
          'success',
          'Please set your new password.'
        );
        setEmailRecovery(prev => ({ ...prev, showPasswordFields: true }));
        setEmailBtnText('Set New Password');
      } else {
        throw new Error(data.message || 'Invalid OTP');
      }
    } catch (error) {
      showAlert(
        'OTP Verification Failed',
        error.message || 'Failed to verify OTP.',
        'fail',
        'Please check the code and try again.'
      );
      setEmailBtnText('Verify OTP');
    }
  };

  const setNewPasswordWithEmail = async (email, otp, newPassword, confirmPassword) => {
    try {
      if (!newPassword || !confirmPassword) {
        throw new Error('Please enter and confirm your new password');
      }

      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (!isValidPassword(newPassword)) {
        throw new Error('Password must be at least 8 characters long and include uppercase, lowercase, number, and special character');
      }

      setEmailBtnText('Setting Password...');

      // Call the password reset endpoint
      const response = await fetch(AUTH_ENDPOINTS.RESET_PASSWORD_WITH_OTP, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, otp, newPassword })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.message || `Server returned ${response.status}: ${response.statusText}`);
        } else {
          throw new Error(`Server returned non-JSON response (${response.status})`);
        }
      }

      const data = await response.json();

      if (data.success) {
        showAlert(
          'Success!',
          'Your password has been reset successfully.',
          'success',
          'You will be redirected to the login page shortly.'
        );
        setTimeout(() => {
          navigate('/login');
        }, 5000); // Redirect after 5 seconds
      } else {
        throw new Error(data.message || 'Failed to reset password');
      }
    } catch (error) {
      showAlert(
        'Password Reset Failed',
        error.message || 'Failed to reset password.',
        'fail',
        'Please try again or contact support.'
      );
      setEmailBtnText('Set New Password');
    }
  };

  const changePasswordWithMnemonics = async (phrases, newPassword) => {
    try {
      setPhraseBtnText('Setting Password...');

      // Call password reset with mnemonic endpoint
      const response = await fetch(AUTH_ENDPOINTS.VERIFY_MNEMONIC_CHANGE_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          mnemonicArray: phrases,
          newPassword
        })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');

        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Server returned ${response.status}: ${response.statusText}`);
        } else {
          throw new Error(`Server returned non-JSON response (${response.status})`);
        }
      }

      const data = await response.json();

      if (data.success) {
        showAlert(
          'Success!',
          'Your password has been reset successfully.',
          'success',
          'You will be redirected to the login page shortly.'
        );
        setTimeout(() => {
          navigate('/login');
        }, 5000); // Redirect after 5 seconds
      } else {
        throw new Error(data.error || 'Failed to reset password');
      }
    } catch (error) {
      showAlert(
        'Password Reset Failed',
        error.message || 'Failed to reset password.',
        'fail',
        'Please try again or contact support.'
      );
      setPhraseBtnText('Verify & Change Password');
    }
  };

  return (
    <>
      <Navbar />

      <div className="main-container">
        <div className="recovery-card">
          <div className="card-header">
            <h1>Password Recovery</h1>
            <p className="text-light">Choose your preferred recovery method</p>
          </div>
          <div className="card-body">
            {/* Recovery method tabs */}
            <div className="recovery-tabs">
              <button
                type="button"
                className={`tab-btn ${activeTab === 'email-tab' ? 'active' : ''}`}
                onClick={() => handleTabChange('email-tab')}
              >
                Email Recovery
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === 'keys-tab' ? 'active' : ''}`}
                onClick={() => handleTabChange('keys-tab')}
              >
                Secret Phrases
              </button>
            </div>

            {/* Email recovery tab */}
            <div id="email-tab" className={`tab-content forgot-tab ${activeTab === 'email-tab' ? 'active' : ''}`}>
              <form onSubmit={handleEmailFormSubmit}>
                <div className="form-group">
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    placeholder="Email Address"
                    value={emailRecovery.email}
                    onChange={handleEmailInputChange}
                    required
                  />
                </div>

                {emailRecovery.showOtp && (
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-control"
                      id="otp"
                      placeholder="Enter OTP"
                      value={emailRecovery.otp}
                      onChange={handleEmailInputChange}
                      required
                    />
                  </div>
                )}

                {emailRecovery.showOtp && emailRecovery.showPasswordFields && (
                  <>
                    <div className="form-group">
                      <PasswordField
                        id="newPassword"
                        placeholder="New Password"
                        value={emailRecovery.newPassword}
                        onChange={handleEmailInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <PasswordField
                        id="confirmPassword"
                        placeholder="Confirm New Password"
                        value={emailRecovery.confirmPassword}
                        onChange={handleEmailInputChange}
                        required
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {emailBtnText}
                  </button>
                </div>
              </form>
            </div>

            {/* Secret phrases tab */}
            <div id="keys-tab" className={`tab-content forgot-tab ${activeTab === 'keys-tab' ? 'active' : ''}`}>
              <form onSubmit={handlePhraseFormSubmit}>
                <div className="phrase-inputs-container">
                  <div className="phrase-column">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div className="form-group" key={i}>
                        <input
                          type="text"
                          className="form-control key-input"
                          placeholder={`Key ${i + 1}`}
                          value={phraseRecovery.phrases[i]}
                          onChange={(e) => handlePhraseInputChange(i, e.target.value)}
                          required
                        />
                      </div>
                    ))}
                  </div>
                  <div className="phrase-column">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div className="form-group" key={i + 6}>
                        <input
                          type="text"
                          className="form-control key-input"
                          placeholder={`Key ${i + 7}`}
                          value={phraseRecovery.phrases[i + 6]}
                          onChange={(e) => handlePhraseInputChange(i + 6, e.target.value)}
                          required
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <PasswordField
                    id="newPassword"
                    placeholder="New Password"
                    value={phraseRecovery.newPassword}
                    onChange={handlePhrasePasswordChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <PasswordField
                    id="confirmPassword"
                    placeholder="Confirm New Password"
                    value={phraseRecovery.confirmPassword}
                    onChange={handlePhrasePasswordChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {phraseBtnText}
                  </button>
                </div>
              </form>
            </div>

            <div className="card-divider"></div>

            <div className="card-links">
              <span className="text-light">Remember your password?</span>
              <Link to="/login" className="card-link">Back to Login</Link>
            </div>
          </div>
        </div>
      </div>

      <Alert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        title={alert.title}
        message={alert.message}
        footer={alert.footer}
        type={alert.type}
      />
    </>
  );
};

export default ForgotPassword;